import React from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  createStyles,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Theme,
} from '@material-ui/core';
import Grid from '@material-ui/core/Grid/Grid';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import { useContext, useDirectBankingMiddlewareApi } from '../../context';
import utils from '../../services/utils.service';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    img: {
      width: 400,
      maxWidth: '100%',
      display: 'block',
      margin: '0 auto',
    },
    noBottomBorder: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
  })
);

type Fee = {
  id: number;
  currency_iso_code_3: string;
  min_amount: number;
  max_amount: number;
  cost_fixed_amount: number;
};

type ConsumerFees = {
  consumer: { id: number; name: string };
  fees: Fee[];
};

const Row = (props: { consumerFees: ConsumerFees; open: boolean; toggleDetails: (c: ConsumerFees) => void }) => {
  const { consumerFees, open, toggleDetails } = props;
  const { t } = useTranslation(['directBankingFees']);
  const classes = useStyles();

  return (
    <React.Fragment>
      <TableRow className={classes.noBottomBorder}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => toggleDetails(consumerFees)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{consumerFees.consumer.name}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box marginBottom={2} marginTop={1}>
              <Typography variant="body1" align="left">{t('FEES')}</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow className={classes.noBottomBorder}>
                      <TableCell>{t('CURRENCY')}</TableCell>
                      <TableCell>{t('MIN_AMOUNT')}</TableCell>
                      <TableCell>{t('MAX_AMOUNT')}</TableCell>
                      <TableCell>{t('FIXED_AMOUNT')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.noBottomBorder}>
                    {consumerFees.fees.map(fee => (
                      <TableRow className={classes.noBottomBorder} key={fee.id}>
                        <TableCell>{fee.currency_iso_code_3}</TableCell>
                        <TableCell>{fee.min_amount}</TableCell>
                        <TableCell>{fee.max_amount}</TableCell>
                        <TableCell>{fee.cost_fixed_amount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export const DirectBankingFees = () => {
  const { t } = useTranslation(['directBankingFees']);
  const context = useContext();
  const api = useDirectBankingMiddlewareApi();
  const [loading, setLoading] = React.useState(true);
  const [consumerFeesList, setConsumerFeesList] = React.useState<ConsumerFees[]>([]);
  const [detailedConsumer, setDetailedConsumer] = React.useState<ConsumerFees | null>(null);

  const classes = useStyles();

  const loadData = () => {
    setLoading(true);
    utils.runAsync(async () => {
      const result = await api.getFees(context.data.isAdmin);
      setConsumerFeesList(result);
      setLoading(false);
    }, () => setLoading(false));
  };

  const toggleDetails = (c: ConsumerFees) => {
    if (detailedConsumer?.consumer.id === c.consumer.id) {
      setDetailedConsumer(null);
    } else {
      setDetailedConsumer(c);
    }
  };

  React.useEffect(loadData, []);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper>
          <Grid container>
            <Grid item xs={12}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>{t('CONSUMER')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {consumerFeesList.map(c =>
                      <Row
                        consumerFees={c}
                        key={c.consumer.id}
                        toggleDetails={toggleDetails}
                        open={detailedConsumer?.consumer.id === c.consumer.id}
                      />
                    )}
                    {loading && consumerFeesList.length === 0 &&
                      <TableCell colSpan={2}>
                        <Box padding={2}>
                          <Grid container justify="center" item xs={12}>
                            <CircularProgress />
                          </Grid>
                        </Box>
                      </TableCell>
                    }
                    {!loading && consumerFeesList.length === 0 &&
                      <TableCell colSpan={2}>
                        <Box padding={2}>
                          <Typography align="center" variant="h5">{t('NO_DATA')}</Typography>
                        </Box>
                      </TableCell>
                    }
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};
